import { AccountOnboarding } from '@adtrackify/at-tracking-event-types';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import {
  OnboardingAcknowledgement,
} from '@/features/setup/types/use-acknowledge-onboarding-step-enums';
import {
  ACKNOWLEDGEMENT_TIMESTAMP_MAP,
} from '@/features/setup/constants/use-acknowledge-onboarding-step-constants';
import { useAuthenticatedMutation } from '@/hooks/auth/use-auth-query-hooks';
import { applyOnboardingToAccountsCache } from '@/features/setup/helpers/onboarding-cache-update';
import { useAccountState } from '@/contexts/account/account-context';
import { AccountsApiService } from '@/services/internal-api/accounts/accounts-api-service';
import type { AccountOnboardingResponse } from '@/services/internal-api/accounts/types/accounts-api-service-types';

function buildBackfillPatch(fields: OnboardingAcknowledgement[]): Partial<AccountOnboarding> {
  const timestamp = new Date().toISOString();
  const patch: Partial<AccountOnboarding> = {};
  for (const field of fields) {
    Object.assign(patch, { [field]: true, [ACKNOWLEDGEMENT_TIMESTAMP_MAP[field]]: timestamp });
  }
  return patch;
}

export function useBackfillOnboardingAcknowledgements() {
  const queryClient = useQueryClient();
  const { accountId } = useAccountState();

  return useAuthenticatedMutation<
    AxiosResponse<AccountOnboardingResponse>,
    unknown,
    OnboardingAcknowledgement[]
  >({
    mutationFn: (fields) => {
      if (!accountId) {
        throw new Error('Cannot backfill onboarding acknowledgements without an account');
      }
      return AccountsApiService.updateAccountOnboarding(accountId, {
        onboarding: buildBackfillPatch(fields),
      });
    },

    onSuccess: (response) => {
      applyOnboardingToAccountsCache(queryClient, accountId ?? '', response.data.onboarding);
    },

    onError: (error) => {
      console.warn('Failed to backfill onboarding acknowledgements', error);
    },
  });
}
